import { useNostr } from '@nostrify/react';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { finalizeEvent } from 'nostr-tools';

import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from './useCurrentUser';
import { getGuestSecretKey } from '@/lib/ground/identity';

export interface GroundEventInput {
  kind: number;
  content: string;
  tags?: string[][];
}

export interface GroundPublishResult {
  event: NostrEvent;
  /** False when the event was signed locally but no relay accepted it. */
  published: boolean;
}

/**
 * Publish a Ground practice event.
 *
 * Uses the Soapbox/Nostrify signer (via `useNostrPublish` conventions) when the
 * user is logged in, and falls back to a stable local demo identity when they
 * are not — so the app works with no login required.
 */
export function useGroundPublish(): UseMutationResult<
  GroundPublishResult,
  Error,
  GroundEventInput
> {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (input: GroundEventInput): Promise<GroundPublishResult> => {
      const tags = [...(input.tags ?? [])];

      if (location.protocol === 'https:' && !tags.some(([name]) => name === 'client')) {
        tags.push(['client', location.hostname]);
      }

      const template = {
        kind: input.kind,
        content: input.content,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      };

      let event: NostrEvent;
      if (user) {
        event = await user.signer.signEvent(template);
      } else {
        event = finalizeEvent(template, getGuestSecretKey()) as NostrEvent;
      }

      let published = false;
      try {
        await nostr.event(event, { signal: AbortSignal.timeout(6000) });
        published = true;
      } catch (error) {
        console.warn('Ground: relay publish failed, kept the event locally.', error);
      }

      return { event, published };
    },
  });
}
